import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Instagram, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => {
  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const OUTCOME_META = {
  would_queue:               { cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40', label: 'Would queue',        icon: CheckCircle2 },
  skipped_past:              { cls: 'bg-amber-500/20 text-amber-300 border-amber-400/40',       label: 'Past date',          icon: AlertTriangle },
  skipped_off_topic:         { cls: 'bg-slate-500/20 text-slate-300 border-slate-400/40',       label: 'Off-topic',          icon: XCircle },
  skipped_unreadable:        { cls: 'bg-rose-500/20 text-rose-300 border-rose-400/40',          label: 'Unreadable',         icon: XCircle },
  skipped_already_seen:      { cls: 'bg-blue-500/20 text-blue-300 border-blue-400/40',          label: 'Already seen',       icon: Info },
  skipped_duplicate_image:   { cls: 'bg-blue-500/20 text-blue-300 border-blue-400/40',          label: 'Duplicate image',    icon: Info },
  skipped_duplicate_semantic:{ cls: 'bg-blue-500/20 text-blue-300 border-blue-400/40',          label: 'Semantic duplicate', icon: Info },
};

export default function AdminScrapeDiagnosePage() {
  const [source, setSource] = useState('');
  const [limit, setLimit] = useState(10);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!source.trim()) {
      toast.error('Enter a handle or #hashtag');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/instagram/scrape/diagnose`,
        { source: source.trim(), limit },
        { headers: authHeaders() },
      );
      setResult(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diagnose failed');
    } finally {
      setBusy(false);
    }
  };

  const totals = result?.totals || {
    fetched: 0, queued: 0, skipped_past: 0, skipped_off_topic: 0,
    skipped_unreadable: 0, skipped_already_seen: 0, skipped_duplicate_semantic: 0,
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-24" data-testid="admin-scrape-diagnose-page">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border/40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/admin" className="rounded-full p-2 hover:bg-muted/40">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Search className="w-5 h-5 text-cyan-400" />
              Scrape diagnose
            </h1>
            <p className="text-xs text-muted-foreground">Dry-run any handle and see per-post outcomes</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-4">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex gap-2">
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="@skateinn_London  or  #rollerdisco"
                className="h-11 rounded-xl flex-1"
                data-testid="diagnose-source-input"
                onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
              />
              <Input
                type="number"
                min={1}
                max={20}
                value={limit}
                onChange={(e) => setLimit(Math.max(1, Math.min(20, parseInt(e.target.value || '10', 10))))}
                className="h-11 rounded-xl w-20"
                data-testid="diagnose-limit-input"
              />
              <Button
                onClick={run}
                disabled={busy}
                className="rounded-full gap-2 h-11"
                data-testid="diagnose-run-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Diagnose
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Runs the exact same Apify → Gemini → dedup pipeline as the auto-scrape cron. No pending items are written. Costs 1 Apify credit + a few Gemini tokens per post.
            </p>
          </CardContent>
        </Card>

        {result && (
          <>
            {/* Auto-scrape config check */}
            <Card
              className={
                result.in_auto_scrape_settings
                  ? 'border-emerald-400/30 bg-emerald-500/5'
                  : 'border-amber-400/40 bg-amber-500/8'
              }
              data-testid="diagnose-config-card"
            >
              <CardContent className="p-4 flex items-start gap-3">
                {result.in_auto_scrape_settings ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="text-sm">
                  {result.in_auto_scrape_settings ? (
                    <>
                      <p className="font-bold text-emerald-200">Included in the daily auto-scrape</p>
                      <p className="text-xs text-muted-foreground mt-0.5">The 03:00 UK cron will hit this source every day.</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-amber-200">NOT in the daily auto-scrape sources</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Currently configured: {result.auto_scrape_sources?.length
                          ? result.auto_scrape_sources.join(', ')
                          : '(none)'}
                      </p>
                      <Link to="/admin/auto-scrape" className="text-xs text-amber-300 underline mt-1 inline-block">Fix that in Auto-scrape settings →</Link>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Totals */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-400" />
                  Result for <span className="text-pink-300">{result.source}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 sm:grid-cols-7 gap-1.5 text-center" data-testid="diagnose-totals">
                {[
                  ['Fetched',        totals.fetched,                     'bg-slate-500/10 text-slate-200 border-slate-400/30'],
                  ['Would queue',    totals.queued,                      'bg-emerald-500/10 text-emerald-300 border-emerald-400/40'],
                  ['Past',           totals.skipped_past,                'bg-amber-500/10 text-amber-300 border-amber-400/30'],
                  ['Off-topic',      totals.skipped_off_topic,           'bg-slate-500/10 text-slate-300 border-slate-400/30'],
                  ['Unreadable',     totals.skipped_unreadable,          'bg-rose-500/10 text-rose-300 border-rose-400/30'],
                  ['Already seen',   totals.skipped_already_seen,        'bg-blue-500/10 text-blue-300 border-blue-400/30'],
                  ['Semantic dupe',  totals.skipped_duplicate_semantic,  'bg-blue-500/10 text-blue-300 border-blue-400/30'],
                ].map(([label, val, cls]) => (
                  <div key={label} className={`rounded-xl border ${cls} py-2`}>
                    <p className="text-lg font-black leading-none">{val}</p>
                    <p className="text-[9px] uppercase tracking-wider mt-0.5">{label}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Per-post breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Per-post</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="diagnose-per-post">
                {(result.per_post || []).length === 0 && (
                  <p className="text-xs text-muted-foreground py-4 text-center" data-testid="diagnose-empty">
                    Apify returned zero posts for this handle. Common reasons: private account, rate-limited, or the handle doesn&apos;t exist.
                  </p>
                )}
                {(result.per_post || []).map((p, i) => {
                  const meta = OUTCOME_META[p.outcome] || OUTCOME_META.skipped_unreadable;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={p.post_url || i}
                      className="flex items-start gap-3 p-3 rounded-xl border border-border/30 bg-muted/10"
                      data-testid={`diagnose-post-${i}`}
                    >
                      {p.image_url && (
                        <img src={p.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[10px] uppercase tracking-wider border ${meta.cls} gap-1`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </Badge>
                          {p.owner && (
                            <span className="text-[10px] text-muted-foreground">@{p.owner}</span>
                          )}
                        </div>
                        <p className="text-xs text-foreground">{p.reason}</p>
                        {p.extracted && (
                          <p className="text-[10px] text-muted-foreground truncate">
                            Extracted: {p.extracted.name || '(no name)'} · {p.extracted.date || '(no date)'} · {p.extracted.sport || '(no sport)'} · {p.extracted.city || '(no city)'}
                          </p>
                        )}
                        {p.caption_preview && (
                          <p className="text-[10px] text-muted-foreground italic truncate">&ldquo;{p.caption_preview}&hellip;&rdquo;</p>
                        )}
                        {p.post_url && (
                          <a
                            href={p.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-cyan-400 underline truncate block"
                          >
                            {p.post_url}
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
